import { X, Lock, Check, Zap, TrendingUp } from "lucide-react";
import { PLAN_INFO, PLAN_FEATURES, PLAN_HIERARCHY } from "../config/planConfig";

const PLAN_ICONS = { basic: "B", pro: "P", business: "BS" };

export default function UpgradeModal({ feature, currentPlan, requiredPlan, onClose, onNavigateAbonnement }) {
  const required = PLAN_INFO[requiredPlan];
  const current = PLAN_INFO[currentPlan];
  if (!required) return null;

  const priceDiff = (required.prijs - current?.prijs).toFixed(2).replace(".", ",");
  const currentIdx = PLAN_HIERARCHY.indexOf(currentPlan);
  const requiredIdx = PLAN_HIERARCHY.indexOf(requiredPlan);
  const stepsUp = requiredIdx - currentIdx;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: required.kleur + "20", border: `1px solid ${required.kleur}30` }}>
              <Lock size={20} style={{ color: required.kleur }} />
            </div>
            <div>
              <p className="text-zinc-500 text-xs font-semibold uppercase tracking-widest mb-0.5">Upgrade vereist</p>
              <h3 className="text-white font-black text-lg">
                Beschikbaar in {required.naam}
              </h3>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          {/* Prijs vergelijking */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 bg-[#111] border border-white/8 rounded-xl p-3 text-center opacity-60">
              <p className="text-zinc-500 text-xs font-semibold mb-1">{current?.naam || "Huidig"}</p>
              <p className="text-white font-black text-lg">€{current?.prijs?.toFixed(2).replace(".", ",")}</p>
              <p className="text-zinc-600 text-xs">per maand</p>
            </div>
            <div className="flex items-center justify-center w-8 shrink-0">
              <TrendingUp size={16} className="text-zinc-600" />
            </div>
            <div className="flex-1 rounded-xl p-3 text-center border"
              style={{ background: required.kleur + "10", borderColor: required.kleur + "30" }}>
              <p className="text-xs font-semibold mb-1" style={{ color: required.kleur }}>{required.naam}</p>
              <p className="text-white font-black text-lg">€{required.prijs.toFixed(2).replace(".", ",")}</p>
              <p className="text-zinc-600 text-xs">per maand</p>
            </div>
          </div>

          {/* Wat je krijgt */}
          <div className="mb-6">
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-3">
              Inbegrepen in {required.naam}
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {(PLAN_FEATURES[requiredPlan] || [])
                .filter(f => !(PLAN_FEATURES[currentPlan] || []).includes(f))
                .map(f => {
                  const labels = {
                    rooster: "Rooster & boekingssysteem",
                    mappen: "Mappen voor schema-organisatie",
                    templates: "Schema templates",
                    credits: "Credits & betaalpakketten",
                    analytics: "Analytics dashboard",
                    abonnement: "Abonnementenbeheer",
                    personeel: "Personeelsbeheer",
                    beheerders: "Meerdere beheerders",
                    facturen: "Facturatie (PDF download)",
                    community: "Community feed",
                  };
                  return (
                    <div key={f} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: required.kleur + "20" }}>
                        <Check size={10} style={{ color: required.kleur }} />
                      </div>
                      <span className="text-zinc-300 text-sm">{labels[f] || f}</span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Sporter limiet */}
          <div className="bg-[#111] border border-white/8 rounded-xl p-3 flex items-center justify-between mb-6">
            <div>
              <p className="text-white text-sm font-semibold">Sporterlimiet verhoogd</p>
              <p className="text-zinc-500 text-xs">
                {current?.max_sporters || 10} → {required.max_sporters} sporters inbegrepen
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold" style={{ color: required.kleur }}>+{required.max_sporters - (current?.max_sporters || 10)} sporters</p>
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:border-white/20 hover:text-white transition-colors">
              Annuleren
            </button>
            <button
              onClick={() => { onNavigateAbonnement?.(); onClose(); }}
              data-testid="upgrade-modal-cta"
              className="flex-1 py-3 rounded-xl text-white text-sm font-black flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ background: required.kleur }}>
              <Zap size={14} />
              Upgrade naar {required.naam}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
